/**
 * AgentBatchLauncher — "Start agent outreach" from a Search run (texting-agent
 * plan §6.13, step 8).
 *
 * One compact modal: target study, one of the two APPROVED re-engagement
 * openers (D-20), pace, start time → PREVIEW (the server preflight: campaign
 * skip rules + agent drops + the D-21 recency warn/hard-stop) → LAUNCH. The
 * launch click is the human approval (§6.13-2); the preview is required first
 * so that approval is informed. Everything stays dark until the owner enables
 * a study — the server refuses with the readiness checklist, which renders
 * here verbatim.
 */
import { useMemo, useState } from 'react';
import { AlertTriangle, Bot, CheckCircle2, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import { fieldCls } from '../filters';
import { primaryBtnCls } from '../filters/styles';
import type { StudyOption } from '../../types';
import { Z } from '../ui';

interface Props {
  runId: string;
  searchName: string | null;
  studies: StudyOption[];
  onClose: () => void;
}

interface BatchPreflight {
  selected: number;
  missing: number;
  to_schedule: number;
  skips: Record<string, number>;
  warnings: { aged: number; oldest_age_days: number; hard_stop_overridden: number };
  recency_override: boolean;
  opener: { key: string; preview: string };
  pace_per_hour: number;
  start_at: string;
  first_send_at: string | null;
  last_send_at: string | null;
  max_targets: number;
}

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

// N-4: canonical field styling (ui/styles via crm/components/filters) — the
// canonical string carries no width by contract, so the modal adds its own.
const inputCls = `mt-1 w-full ${fieldCls}`;
const labelCls = 'block text-xs font-medium text-gray-600 dark:text-gray-300';

const OPENERS = [
  {
    key: 'texting_agent.template.reengage_recent',
    label: 'Recent lead — "you reached out about {{study}} a while back"',
  },
  {
    key: 'texting_agent.template.reengage_aged',
    label: 'Aged lead — "a new study opened that may line up"',
  },
];

const SKIP_LABELS: Record<string, string> = {
  opted_out: 'Opted out / consent revoked',
  no_channel: 'No phone on file',
  no_consent_basis: 'No consent basis (imported/manual, never texted us)',
  recently_contacted: 'Contacted in the last 7 days',
  active_elsewhere: 'Active in another study',
  archived: 'Archived',
  batch_excluded: 'Excluded (wrong-person reply)',
  agent_conversation_open: 'Agent already talking to them',
  protected_lifecycle: 'Scheduled / randomized',
  terminal_attempt: 'Already ran this study to an outcome',
  too_old: 'Older than 12 months (hard stop)',
};

/** Local datetime-input value for now+deltaMin, minute-floored. */
function localDatetimeValue(deltaMin: number): string {
  const d = new Date(Date.now() + deltaMin * 60000);
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AgentBatchLauncher({ runId, searchName, studies, onClose }: Props) {
  const [studyId, setStudyId] = useState('');
  const [openerKey, setOpenerKey] = useState(OPENERS[0].key);
  const [pace, setPace] = useState(20);
  const [startAt, setStartAt] = useState(() => localDatetimeValue(15));
  const [override, setOverride] = useState(false);
  const [preflight, setPreflight] = useState<BatchPreflight | null>(null);
  const [readiness, setReadiness] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'preview' | 'launch' | null>(null);
  const [launched, setLaunched] = useState<{ batchId: string; scheduled: number } | null>(null);

  const crmStudies = useMemo(
    () => studies
      .filter((s) => Boolean(s.id))
      .sort((a, b) => String(a.internal_title || a.title || a.slug).localeCompare(String(b.internal_title || b.title || b.slug))),
    [studies],
  );

  const body = () => ({
    study_id: studyId,
    search_run_id: runId,
    opener_template_key: openerKey,
    pace_per_hour: pace,
    start_at: new Date(startAt).toISOString(),
    recency_override: override,
    description: searchName || undefined,
  });

  const preview = async () => {
    setBusy('preview');
    setError(null);
    setReadiness(null);
    try {
      const res = await api.post('/api/crm/agent/batches', { ...body(), dry_run: true });
      setPreflight(res.data?.preflight || null);
    } catch (e: any) {
      setPreflight(null);
      setReadiness(e?.response?.data?.readiness || null);
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const launch = async () => {
    setBusy('launch');
    setError(null);
    try {
      const res = await api.post('/api/crm/agent/batches', body());
      setLaunched({ batchId: res.data?.batch?.id, scheduled: res.data?.scheduled || 0 });
    } catch (e: any) {
      setReadiness(e?.response?.data?.readiness || null);
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  // Any input change invalidates the previewed report the launch click approves.
  const invalidate = () => { setPreflight(null); };

  const skipEntries = preflight
    ? Object.entries(preflight.skips).filter(([, n]) => n > 0)
    : [];

  return (
    <div className={`fixed inset-0 ${Z.modal} flex items-center justify-center bg-black/40 p-4`} onClick={onClose} role="presentation">
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-gray-800"
        role="dialog"
        aria-modal="true"
        aria-label="Start agent outreach"
      >
        <div className="flex items-center gap-2 border-b border-gray-200 p-4 dark:border-gray-700">
          <Bot className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Start agent outreach</h2>
          <span className="ml-auto text-xs text-gray-400">{searchName || 'this list'}</span>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {launched ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Batch launched — {launched.scheduled.toLocaleString()} opener{launched.scheduled === 1 ? '' : 's'} scheduled.
              </p>
              <p className="max-w-sm text-xs text-gray-500 dark:text-gray-400">
                Replies open agent conversations automatically; watch them (and cancel remaining sends) in the Agent console's Batches tab.
              </p>
            </div>
          ) : (
            <>
              <div>
                <label className={labelCls} htmlFor="ab-study">Target study (must be agent-enabled and ready)</label>
                <select id="ab-study" value={studyId} onChange={(e) => { setStudyId(e.target.value); invalidate(); }} className={inputCls}>
                  <option value="">Choose a study…</option>
                  {crmStudies.map((s) => (
                    <option key={s.id} value={s.id || ''}>{s.internal_title || s.title || s.slug}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="ab-opener">Opener (approved template — sent as-is)</label>
                <select id="ab-opener" value={openerKey} onChange={(e) => { setOpenerKey(e.target.value); invalidate(); }} className={inputCls}>
                  {OPENERS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls} htmlFor="ab-pace">Pace (texts/hour)</label>
                  <input
                    id="ab-pace" type="number" min={1} max={600} value={pace}
                    onChange={(e) => { setPace(Math.max(1, parseInt(e.target.value, 10) || 1)); invalidate(); }}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="ab-start">Start</label>
                  <input
                    id="ab-start" type="datetime-local" value={startAt}
                    onChange={(e) => { setStartAt(e.target.value); invalidate(); }}
                    className={inputCls}
                  />
                </div>
              </div>

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                  {error}
                  {Array.isArray(readiness?.items) && (
                    <ul className="mt-1.5 space-y-0.5 text-xs">
                      {readiness.items.filter((i: any) => i.blocking && !i.ok).map((i: any) => (
                        <li key={i.key}>• {i.key.replace(/_/g, ' ')}: {i.detail}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {preflight && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-900/40">
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {preflight.to_schedule.toLocaleString()} of {preflight.selected.toLocaleString()} will get the opener
                  </p>
                  {skipEntries.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {skipEntries.map(([reason, n]) => (
                        <li key={reason}>− {n.toLocaleString()} · {SKIP_LABELS[reason] || reason.replace(/_/g, ' ')}</li>
                      ))}
                      {preflight.missing > 0 && <li>− {preflight.missing.toLocaleString()} · merged / not in this network</li>}
                    </ul>
                  )}
                  {preflight.warnings.aged > 0 && (
                    <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                      {preflight.warnings.aged.toLocaleString()} lead{preflight.warnings.aged === 1 ? ' is' : 's are'} older than 90 days
                      (oldest {preflight.warnings.oldest_age_days}d) — aged numbers may have changed hands.
                    </p>
                  )}
                  {(preflight.skips.too_old || 0) > 0 && (
                    <label className="mt-2 flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={override}
                        onChange={(e) => { setOverride(e.target.checked); setPreflight(null); }}
                        className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Include the {preflight.skips.too_old} lead{preflight.skips.too_old === 1 ? '' : 's'} older than 12 months anyway (higher wrong-number risk)
                    </label>
                  )}
                  {preflight.first_send_at && (
                    <p className="mt-2 text-xs text-gray-400">
                      Sends {new Date(preflight.first_send_at).toLocaleString()} → {preflight.last_send_at ? new Date(preflight.last_send_at).toLocaleString() : '—'} at {preflight.pace_per_hour}/hr
                    </p>
                  )}
                  <p className="mt-2 whitespace-pre-wrap rounded-lg border border-gray-200 bg-white p-2 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    {preflight.opener.preview}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 p-4 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
          >
            {launched ? 'Done' : 'Cancel'}
          </button>
          {!launched && (preflight ? (
            <button
              type="button"
              disabled={busy !== null || preflight.to_schedule === 0}
              onClick={launch}
              className={primaryBtnCls}
            >
              {busy === 'launch' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bot className="h-4 w-4" />}
              Launch {preflight.to_schedule.toLocaleString()} opener{preflight.to_schedule === 1 ? '' : 's'}
            </button>
          ) : (
            <button
              type="button"
              disabled={busy !== null || !studyId}
              onClick={preview}
              className={primaryBtnCls}
            >
              {busy === 'preview' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Preview
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
